import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import cn from './SlotPopover.module.scss'

const GAP = 8
const VIEWPORT_PADDING = 12

type SlotPopoverProps = {
  anchor: HTMLElement | null
  onClose: () => void
  children: ReactNode
}

function SlotPopover({ anchor, onClose, children }: SlotPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{
    left: number
    bottom: number
  } | null>(null)

  const updatePosition = useCallback(() => {
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const width = popoverRef.current?.offsetWidth ?? 0
    const maxLeft = window.innerWidth - width - VIEWPORT_PADDING
    setPosition({
      left: Math.max(VIEWPORT_PADDING, Math.min(rect.left, maxLeft)),
      bottom: window.innerHeight - rect.top + GAP,
    })
  }, [anchor])

  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  useEffect(() => {
    if (!anchor) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (popoverRef.current?.contains(target) || anchor.contains(target))
        return
      onClose()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchor, onClose, updatePosition])

  if (!anchor) return null

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      className={`${cn.slotPopover} ${position ? '' : cn.slotPopoverHidden}`}
      style={{
        '--popover-left': `${position?.left ?? 0}px`,
        '--popover-bottom': `${position?.bottom ?? 0}px`,
      }}
    >
      {children}
    </div>,
    document.body
  )
}

export default SlotPopover
